import { Badge, type BadgeVariant } from "@dl/web-core-js/ui";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import type { ProductionOperationStatus } from "../../api/floor";
import type { ProductionOrderKind, ProductionOrderStatus } from "../../api/production";

const STATUS_VARIANT: Record<ProductionOrderStatus, BadgeVariant> = {
  PLANNED: "neutral",
  FIRMED: "warning",
  RELEASED: "info",
  IN_PROGRESS: "info",
  PRODUCED: "accent",
  SHIPPING: "warning",
  COMPLETED: "success",
  CLOSED: "success",
  CANCELLED: "error",
};

/** An order's status as the plant says it: grey planned, blue under way, green finished. */
export function ProductionStatusBadge({ status }: { status: ProductionOrderStatus }) {
  const { t } = useTranslation();
  return (
    <Badge variant={STATUS_VARIANT[status]} size="sm">
      {productionStatusLabel(t, status)}
    </Badge>
  );
}

export function productionStatusLabel(t: (key: string, fallback: string) => string, status: ProductionOrderStatus): string {
  return {
    PLANNED: t("mo.status.planned", "Planned"),
    FIRMED: t("mo.status.firmed", "Sets arrived"),
    RELEASED: t("mo.status.in-production", "In production"),
    IN_PROGRESS: t("mo.status.in-progress", "Assembling"),
    PRODUCED: t("mo.status.produced", "Production completed"),
    SHIPPING: t("mo.status.shipping", "Shipping"),
    COMPLETED: t("mo.status.completed", "Completed"),
    CLOSED: t("mo.status.closed", "Closed"),
    CANCELLED: t("mo.status.cancelled", "Cancelled"),
  }[status];
}

export function productionKindLabel(t: TFunction, kind: ProductionOrderKind): string {
  return {
    FABRICATION: t("mo.kind.fabrication", "Production"),
    ASSEMBLY: t("mo.kind.assembly", "Assembly"),
    KANBAN: t("mo.kind.kanban", "Kanban"),
    SERVICE: t("mo.kind.service", "Service"),
  }[kind];
}

const STEP_VARIANT: Record<ProductionOperationStatus, BadgeVariant> = {
  PENDING: "neutral",
  ACTIVE: "info",
  DONE: "success",
  SKIPPED: "neutral",
};

export function stepStatusLabel(t: TFunction, status: ProductionOperationStatus): string {
  return {
    PENDING: t("floor.step.pending", "To do"),
    ACTIVE: t("floor.step.active", "In progress"),
    DONE: t("floor.step.done", "Done"),
    SKIPPED: t("floor.step.skipped", "Skipped"),
  }[status];
}

export function StepStatusBadge({ status }: { status: ProductionOperationStatus }) {
  const { t } = useTranslation();
  return (
    <Badge variant={STEP_VARIANT[status]} size="sm">
      {stepStatusLabel(t, status)}
    </Badge>
  );
}

/**
 * An order's statuses in order, for steppers; CANCELLED is not a step. A
 * production order ends when everything it made has shipped, an assembly
 * order when it is on a shelf, a kanban or service lot at Laval.
 */
export function lifecycleOf(kind: ProductionOrderKind): ProductionOrderStatus[] {
  switch (kind) {
    case "FABRICATION":
      return ["PLANNED", "RELEASED", "PRODUCED", "SHIPPING", "CLOSED"];
    case "ASSEMBLY":
      return ["PLANNED", "FIRMED", "IN_PROGRESS", "COMPLETED"];
    default:
      return ["PLANNED", "RELEASED", "PRODUCED", "COMPLETED"];
  }
}

/** An order made from SIGM: finished as SIGM described it, its bill and header locked. */
export function SigmBadge() {
  const { t } = useTranslation();
  return (
    <span title={t("mo.sigm.title", "Made from a SIGM project: its bill, split and header are locked; the floor still reports on it.")}>
      <Badge variant="warning" size="sm">
        {t("mo.sigm", "From SIGM")}
      </Badge>
    </span>
  );
}
